import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useState, useCallback } from 'react';

import { environment } from '@env';

import { useAuth } from '@/context/AuthProvider';

const ENDPOINT = 'document/google-drive/stream-staged-folder-upload';

export interface StreamStagedFolderUploadRequest {
  folderPath: string;
  locationId: number;
  folderId?: number;
}

export interface StreamProgressEvent {
  status: string;
  message: string;
  fileName?: string;
  fileIndex?: number;
  totalFiles?: number;
  progress?: number;
  folderPath?: string;
  locationId?: number;
  organizationMethod?: string;
  error?: string;
  type?: string; // For filtering padding events
  action?: string; // For handling specific actions like 'refresh_vault'
}

export interface StreamState {
  isStreaming: boolean;
  progress: number;
  currentFile?: string;
  totalFiles?: number;
  processedFiles: number;
  errors: string[];
  isCompleted: boolean;
  isFailed: boolean;
}

export const useStreamStagedFolderUpload = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const [streamState, setStreamState] = useState<StreamState>({
    isStreaming: false,
    progress: 0,
    processedFiles: 0,
    errors: [],
    isCompleted: false,
    isFailed: false,
  });

  const resetState = useCallback(() => {
    setStreamState({
      isStreaming: false,
      progress: 0,
      processedFiles: 0,
      errors: [],
      isCompleted: false,
      isFailed: false,
    });
  }, []);

  const invalidateQueries = useCallback(() => {
    // Invalidate all relevant queries to refresh the UI
    queryClient.invalidateQueries({ queryKey: ['GET', 'document/google-drive/staged-files'] });
    queryClient.invalidateQueries({ queryKey: ['GET', 'vault/documents'] });
    queryClient.invalidateQueries({ queryKey: ['folders'] });
    queryClient.invalidateQueries({ queryKey: ['folders', 'hierarchy'] });
    queryClient.invalidateQueries({ queryKey: ['folders', 'contents'] });
    queryClient.invalidateQueries({ queryKey: ['folders', 'root-contents'] });
    queryClient.invalidateQueries({ queryKey: ['GET', 'vault/document-categories'] });

    // Force refetch of current page data
    queryClient.refetchQueries({ queryKey: ['GET', 'vault/documents'] });
    queryClient.refetchQueries({ queryKey: ['GET', 'document/google-drive/staged-files'] });
  }, [queryClient]);

  const startStream = useCallback(
    async (request: StreamStagedFolderUploadRequest) => {
      if (!session?.access_token) {
        enqueueSnackbar('Authentication required', { variant: 'error' });
        return;
      }

      resetState();

      setStreamState(prev => ({
        ...prev,
        isStreaming: true,
      }));

      try {
        const response = await fetch(`${environment.api}/${ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Failed to get stream reader');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        let isStreamComplete = false;
        const startTime = Date.now();
        const TIMEOUT_MS = 300000; // 5 minutes

        // eslint-disable-next-line no-constant-condition
        while (true && !isStreamComplete) {
          // Check for timeout
          if (Date.now() - startTime > TIMEOUT_MS) {
            setStreamState(prev => ({
              ...prev,
              isStreaming: false,
              isFailed: true,
              errors: [...prev.errors, 'Stream timeout after 5 minutes'],
            }));
            enqueueSnackbar('Stream timed out', { variant: 'error' });
            break;
          }

          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();

          if (done) {
            // If stream ends without [DONE], still mark as complete if we processed files
            setStreamState(prev => ({
              ...prev,
              isStreaming: false,
              isCompleted: true,
            }));
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              if (data === '[DONE]') {
                setStreamState(prev => ({
                  ...prev,
                  isStreaming: false,
                  isCompleted: true,
                }));
                isStreamComplete = true;
                break;
              }

              try {
                const event = JSON.parse(data) as StreamProgressEvent;

                // Process non-padding events only
                if (event.type !== 'padding') {
                  // Check for completion before updating state
                  const isCompleting = event.status === 'completed';
                  const isErroring = event.status === 'error';

                  setStreamState(prev => {
                    const newState = { ...prev };

                    // Update progress
                    if (event.progress !== undefined) {
                      newState.progress = event.progress;
                    }

                    // Update current file info
                    if (event.fileName) {
                      newState.currentFile = event.fileName;
                    }

                    // Update total files
                    if (event.totalFiles !== undefined) {
                      newState.totalFiles = event.totalFiles;
                    }

                    // Track processed files
                    if (event.status === 'file_completed') {
                      newState.processedFiles = event.fileIndex || 0;
                    }

                    // Handle errors
                    if (event.status === 'file_error' && event.error) {
                      newState.errors = [...prev.errors, `${event.fileName}: ${event.error}`];
                    } else if (isErroring) {
                      newState.errors = [...prev.errors, event.message];
                      newState.isFailed = true;
                      newState.isStreaming = false;
                    }

                    // Handle completion
                    if (isCompleting) {
                      newState.isCompleted = true;
                      newState.isStreaming = false;
                      newState.progress = 100;
                    }

                    return newState;
                  });

                  // Set completion flag outside of state setter
                  if (isCompleting || isErroring) {
                    isStreamComplete = true;
                  }

                  // Handle real-time vault refresh when files are organized
                  if (event.action === 'refresh_vault') {
                    // Trigger immediate vault refresh when a file is organized
                    invalidateQueries();
                  }

                  // Show notifications for specific events
                  if (event.status === 'completed') {
                    enqueueSnackbar(
                      `✅ Successfully processed all ${event.totalFiles} files from "${event.folderPath}"`,
                      { variant: 'success' }
                    );
                    invalidateQueries();
                    isStreamComplete = true;
                    break; // Break out of the for loop when completed
                  } else if (event.status === 'error') {
                    enqueueSnackbar(event.message, { variant: 'error' });
                    isStreamComplete = true;
                    break; // Break out of the for loop on error
                  } else if (event.status === 'file_error') {
                    enqueueSnackbar(`❌ Error processing "${event.fileName}": ${event.error}`, {
                      variant: 'warning',
                      autoHideDuration: 3000,
                    });
                  } else if (event.status === 'organized') {
                    // Show success notification when a file is organized
                    enqueueSnackbar(`✅ "${event.fileName}" organized successfully`, {
                      variant: 'success',
                      autoHideDuration: 2000,
                    });
                  }
                }
              } catch (parseError) {
                // Silently ignore parse errors to comply with no-console rule
              }
            }
          }

          // If we completed the stream, break out of the while loop
          if (isStreamComplete) {
            break;
          }
        }

        // Give the UI a moment to show the completion state
        if (isStreamComplete) {
          await new Promise(resolve => {
            setTimeout(() => resolve(undefined), 1000);
          });
        }
      } catch (error) {
        setStreamState(prev => ({
          ...prev,
          isStreaming: false,
          isFailed: true,
          errors: [...prev.errors, `Stream error: ${(error as Error).message}`],
        }));
        enqueueSnackbar(`Failed to start folder upload: ${(error as Error).message}`, {
          variant: 'error',
        });
      }
    },
    [session?.access_token, resetState, invalidateQueries]
  );

  return {
    startStream,
    streamState,
    resetState,
  };
};
